/**
 * UnifiedMemoPanel Component
 * V152: 통합 메모 관리 컴포넌트
 * V191: #122 상담사 정보 자동 기록, #132 수정 일시 표시
 * V192: 메모 수정 기능 제거 (히스토리 없이 수정 불가 정책)
 *
 * 모든 엔티티(고객, 상담, 예약)에서 동일한 UI/UX 제공
 * 요구사항: 메모는 항상 작성시간, 작성자, 내용이 표시되어야 함
 * 정책: 메모는 추가/삭제만 가능, 수정 불가 (히스토리 관리 없으므로)
 */

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2, FileText, User, Clock, Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { formatDateTimeWithDay } from '@/utils/dateFormatter';
import { useToast } from '@/components/ui/use-toast';
import { memoApi } from '@/api/memo';
import type { MemoDto, MemoEntityType } from '@/types/memo';

interface UnifiedMemoPanelProps {
  /**
   * 메모가 연결된 엔티티 타입
   */
  entityType: MemoEntityType;
  /**
   * 메모가 연결된 엔티티 ID
   */
  entityId: number;
  /**
   * 읽기 전용 모드
   */
  readOnly?: boolean;
  /**
   * 제목 (기본: "메모")
   */
  title?: string;
  /**
   * 데이터 변경 시 콜백
   */
  onDataChange?: () => void;
}

export function UnifiedMemoPanel({
  entityType,
  entityId,
  readOnly = false,
  title = '메모',
  onDataChange,
}: UnifiedMemoPanelProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // 쿼리 키
  const queryKey = ['memos', entityType, entityId];

  // 메모 목록 조회
  const { data: memos = [], isLoading, error } = useQuery({
    queryKey,
    queryFn: () => memoApi.getMemosByEntity(entityType, entityId),
    enabled: !!entityId,
  });

  // 메모 추가 상태
  const [newMemoContent, setNewMemoContent] = useState('');

  // 메모 삭제 상태
  const [deletingMemo, setDeletingMemo] = useState<MemoDto | null>(null);

  // 메모 추가 뮤테이션
  const addMutation = useMutation({
    mutationFn: (content: string) =>
      memoApi.createMemo({
        entityType,
        entityId,
        content,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      setNewMemoContent('');
      toast({ title: '메모가 추가되었습니다.' });
      onDataChange?.();
    },
    onError: () => {
      toast({ title: '메모 추가 실패', variant: 'destructive' });
    },
  });

  // 메모 삭제 뮤테이션
  const deleteMutation = useMutation({
    mutationFn: (memoId: number) => memoApi.deleteMemo(memoId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      setDeletingMemo(null);
      toast({ title: '메모가 삭제되었습니다.' });
      onDataChange?.();
    },
    onError: () => {
      toast({ title: '메모 삭제 실패', variant: 'destructive' });
    },
  });

  // 메모 추가 핸들러
  const handleAddMemo = () => {
    if (!newMemoContent.trim()) {
      toast({
        variant: 'destructive',
        title: '입력 오류',
        description: '메모 내용을 입력해주세요.',
      });
      return;
    }
    addMutation.mutate(newMemoContent.trim());
  };

  // 메모 삭제 핸들러
  const handleDeleteMemo = () => {
    if (!deletingMemo) return;
    deleteMutation.mutate(deletingMemo.id);
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            {title}
          </CardTitle>
        </CardHeader>
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            {title}
          </CardTitle>
        </CardHeader>
        <CardContent className="text-center py-8 text-muted-foreground text-sm">
          메모를 불러오는 중 오류가 발생했습니다.
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            {title} ({memos.length}개)
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* 메모 목록 */}
          {memos.length > 0 ? (
            <div className="space-y-3">
              {memos.map((memo) => (
                <div
                  key={memo.id}
                  className="border rounded-lg p-3 hover:bg-accent/5 transition-colors"
                >
                  <div className="flex items-start justify-between gap-2">
                    <p className="flex-1 whitespace-pre-wrap text-sm">
                      {memo.content}
                    </p>
                    {/* V192: 메모는 추가/삭제만 가능 (수정 불가 - 히스토리 관리 없음) */}
                    {!readOnly && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 text-destructive hover:text-destructive flex-shrink-0"
                        onClick={() => setDeletingMemo(memo)}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    )}
                  </div>
                  {/* 작성 정보 (필수 표시: 작성시간, 작성자) */}
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground mt-2">
                    <span className="flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      {formatDateTimeWithDay(memo.createdAt)}
                    </span>
                    {memo.createdByName && (
                      <span className="flex items-center gap-1">
                        <User className="h-3 w-3" />
                        {memo.createdByName}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground text-sm">
              등록된 메모가 없습니다
            </div>
          )}

          {/* 메모 추가 폼 */}
          {!readOnly && (
            <div className="space-y-2 pt-4 border-t">
              <Textarea
                placeholder="새 메모를 입력하세요..."
                value={newMemoContent}
                onChange={(e) => setNewMemoContent(e.target.value)}
                rows={3}
                disabled={addMutation.isPending}
              />
              <div className="flex justify-end">
                <Button
                  onClick={handleAddMemo}
                  disabled={addMutation.isPending || !newMemoContent.trim()}
                  className="gap-2"
                >
                  {addMutation.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Plus className="h-4 w-4" />
                  )}
                  메모 추가
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* 메모 삭제 확인 다이얼로그 */}
      <AlertDialog
        open={deletingMemo !== null}
        onOpenChange={(open) => {
          if (!open) setDeletingMemo(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>메모 삭제</AlertDialogTitle>
            <AlertDialogDescription>
              이 메모를 삭제하시겠습니까? 이 작업은 되돌릴 수 없습니다.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteMutation.isPending}>
              취소
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteMemo}
              disabled={deleteMutation.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteMutation.isPending ? '삭제 중...' : '삭제'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
